import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn } from "../components/UI";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, Circle, Sparkles, ArrowRight, PartyPopper
} from "lucide-react";

export default function OnboardingChecklist() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api.get("/political-os/onboarding/checklist")
      .then(r => setData(r.data))
      .catch(e => toast.error(errMsg(e, "Could not load checklist")))
      .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  if (loading || !data) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;

  if (!data.has_entity) {
    return (
      <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="onboarding-no-entity">
        <PageHeader eyebrow="Getting Started" title="Set up your political entity first" subtitle="TotalReach needs to know which campaign you represent before we can start the onboarding checklist." />
        <div className="mt-4">
          <Btn onClick={() => navigate("/onboarding/political")} data-testid="checklist-start-onboarding-btn">
            <Sparkles className="w-4 h-4" /> Start onboarding
          </Btn>
        </div>
      </div>
    );
  }

  const pct = Math.round((data.progress || 0) * 100);

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto space-y-6" data-testid="onboarding-checklist-page">
      <PageHeader
        eyebrow="Getting Started"
        title={data.complete ? `${data.entity_name} is fully launched` : `Launch checklist for ${data.entity_name}`}
        subtitle={data.complete
          ? "Every core setup step is done — you're ready to run this race."
          : "Eight setup steps to run a full-stack campaign. Every one unlocks a real capability — no busywork."}
      />

      {data.complete ? (
        <Card data-testid="checklist-complete-card" className="border-emerald-400/40 bg-emerald-400/5">
          <div className="flex items-start gap-3">
            <PartyPopper className="w-8 h-8 text-emerald-300 shrink-0" />
            <div>
              <div className="font-display text-lg font-bold text-white">You&apos;re fully set up.</div>
              <div className="text-sm text-slate-300 mt-1">
                Every step in the checklist is done. Head to your dashboard to run the campaign, or open the AI Chief of Staff to start planning next week.
              </div>
              <div className="flex gap-2 mt-3">
                <Btn onClick={() => navigate("/dashboard")} data-testid="checklist-open-dashboard-btn">
                  Open dashboard
                </Btn>
                <Btn onClick={() => navigate("/ask-totalreach")} variant="secondary" data-testid="checklist-ask-btn">
                  Ask TotalReach
                </Btn>
              </div>
            </div>
          </div>
        </Card>
      ) : (
        <Card data-testid="checklist-progress-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Progress</div>
              <div className="font-display text-2xl font-bold text-white mt-1" data-testid="checklist-progress-count">
                {data.done_count} / {data.total_steps} <span className="text-slate-500 text-base font-medium">complete</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-500">Verification score</div>
              <div className="text-cyan-300 font-bold text-xl">{data.verification_score || 0}<span className="text-xs text-slate-500 font-normal">/100</span></div>
            </div>
          </div>
          <div className="h-2 rounded-full bg-slate-800 overflow-hidden" data-testid="checklist-progress-bar">
            <div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          {data.next_action && (
            <div className="mt-4 flex items-center justify-between p-3 rounded-lg bg-cyan-400/5 border border-cyan-400/30">
              <div className="text-sm text-slate-200">Next up: <span className="font-semibold text-white">{data.next_action.label}</span></div>
              <button onClick={() => navigate(data.next_action.url)} data-testid="checklist-next-action-btn"
                className="text-xs px-3 py-1.5 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold inline-flex items-center gap-1">
                Continue <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </Card>
      )}

      <Card padded={false} data-testid="checklist-steps-card">
        <div className="divide-y divide-white/5">
          {(data.steps || []).map((s) => (
            <div key={s.key} className="p-5 flex items-start gap-4" data-testid={`checklist-step-${s.key}`}>
              <div className="shrink-0 mt-0.5">
                {s.done
                  ? <CheckCircle2 className="w-6 h-6 text-emerald-400" data-testid={`checklist-step-done-${s.key}`} />
                  : <Circle className="w-6 h-6 text-slate-600" />}
              </div>
              <div className="flex-1">
                <div className={`font-display text-base font-semibold ${s.done ? "text-slate-400 line-through decoration-slate-700" : "text-white"}`}>
                  {s.label}
                </div>
                <div className="text-xs text-slate-500 mt-1 leading-relaxed">{s.description}</div>
              </div>
              {!s.done && (
                <button onClick={() => navigate(s.action_url)} data-testid={`checklist-step-cta-${s.key}`}
                  className="shrink-0 text-xs px-3 py-1.5 rounded-full border border-cyan-400/40 text-cyan-200 hover:bg-cyan-400/10 inline-flex items-center gap-1">
                  {s.cta} <ArrowRight className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
